// src/components/staff-time/StaffKiosk.jsx
// Staff check-in kiosk — embedded in FrontDesk as the Staff tab
// Location is always "center" — no selection needed

import React, { useState, useEffect, useRef } from 'react';
import { Clock, Coffee, LogOut, LogIn } from 'lucide-react';
import { getAllStaff, checkIn, checkOut, startBreak, endBreak } from '../../services/staffService';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

// Location is now selected by staff at check-in
const AUTO_BREAK_CLOSE_MINUTES = 30;
const FALLBACK_AUTO_CHECKOUT_HOUR = 18; // 6 PM fallback

const fmt12 = (date) => {
  if (!date) return '—';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const parseTime12 = (timeStr) => {
  // Parse "3:00 PM" or "15:00" to { hours, minutes }
  if (!timeStr) return null;
  const upper = timeStr.toUpperCase().trim();
  const isPM = upper.includes('PM');
  const isAM = upper.includes('AM');
  const clean = upper.replace('AM', '').replace('PM', '').trim();
  const [h, m] = clean.split(':').map(Number);
  if (isNaN(h)) return null;
  let hours = h;
  if (isPM && h !== 12) hours += 12;
  if (isAM && h === 12) hours = 0;
  return { hours, minutes: m || 0 };
};

const StaffKiosk = () => {
  const [staff, setStaff] = useState([]);
  const [activeEntries, setActiveEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('redmond');
  const [staffShiftHours, setStaffShiftHours] = useState({}); // staffId -> shift hours today
  const [currentTime, setCurrentTime] = useState(new Date());
  const messageTimerRef = useRef(null);
  const processingRef = useRef({});

  const getTodayKey = () => {
    const now = new Date();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const y = now.getFullYear();
    return `${m}-${d}-${y}`;
  };

  // Load today's scheduled shift hours for all staff
  const loadTodayShifts = async () => {
    try {
      const today = new Date();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const y = today.getFullYear();
      const y2 = String(y).slice(-2);
      const dateStr4 = m + '-' + d + '-' + y;
      const dateStr2 = m + '-' + d + '-' + y2;
      const parseH = (t) => {
        if (!t) return null;
        const match = String(t).toUpperCase().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
        if (!match) return null;
        let h = parseInt(match[1]), mn = parseInt(match[2]);
        if (match[3] === 'PM' && h !== 12) h += 12;
        if (match[3] === 'AM' && h === 12) h = 0;
        return h + mn / 60;
      };
      // Try 4-digit year first, then 2-digit
      let snap = await getDocs(query(collection(db, 'schedules'), where('date', '==', dateStr4)));
      if (snap.empty) snap = await getDocs(query(collection(db, 'schedules'), where('date', '==', dateStr2)));
      const hoursMap = {};
      snap.docs.forEach(doc => {
        const s = doc.data();
        if (!s.staffId) return;
        const start = parseH(s.startTime);
        const end = parseH(s.endTime);
        if (start !== null && end !== null) hoursMap[s.staffId] = end - start;
      });
      setStaffShiftHours(hoursMap);
    } catch (err) { console.error('Error loading shift hours:', err); }
  };

  const displayName = (s) => `${s.firstName} ${(s.lastName || '').charAt(0)}.`;

  const getBreakLabel = (entry) => {
    if (!entry || entry.status !== 'on-break') return null;
    const activeBreak = [...(entry.breaks || [])].reverse().find(b => !b.endTime);
    if (!activeBreak) return '☕ On Break';
    const isLunch = activeBreak.type === 'unpaid-meal';
    const startMs = activeBreak.startTime?.toMillis
      ? activeBreak.startTime.toMillis()
      : activeBreak.startTime ? new Date(activeBreak.startTime).getTime() : null;
    // Use currentTime to trigger reactive re-render every second
    const elapsed = startMs ? Math.floor((currentTime.getTime() - startMs) / 60000) : null;
    const label = isLunch ? '🍽️ Lunch Break' : '☕ 10-Min Break';
    const overLimit = isLunch ? elapsed > 35 : elapsed > 12;
    return elapsed !== null
      ? `${label} · ${elapsed}m${overLimit ? ' ⚠️' : ''}`
      : label;
  };

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      const todayKey = getTodayKey();
      const [staffList, entriesSnap] = await Promise.all([
        getAllStaff(),
        getDocs(query(
          collection(db, 'time_entries'),
          where('status', 'in', ['checked-in', 'on-break']),
          where('date', '==', todayKey)
        )),
      ]);
      const activeMap = {};
      entriesSnap.docs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        activeMap[data.staffId] = data;
      });
      setStaff(staffList.filter(s => s.active !== false));
      setActiveEntries(activeMap);
    } catch (err) {
      console.error('Kiosk load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    messageTimerRef.current = setTimeout(() => setMessage(null), 3000);
  };

  // Get scheduled end time for a staff member today from 'schedules' collection
  const getScheduledEndTime = async (staffId) => {
    try {
      const today = new Date();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const y = today.getFullYear();
      const dateKey = `${m}-${d}-${y}`;
      const snap = await getDocs(query(
        collection(db, 'schedules'),
        where('staffId', '==', staffId),
        where('date', '==', dateKey)
      ));
      if (snap.empty) return null;
      // Find latest end time across shifts today
      let latestEnd = null;
      snap.docs.forEach(doc => {
        const endTime = doc.data().endTime;
        const parsed = parseTime12(endTime);
        if (parsed) {
          if (!latestEnd || parsed.hours > latestEnd.hours ||
            (parsed.hours === latestEnd.hours && parsed.minutes > latestEnd.minutes)) {
            latestEnd = parsed;
          }
        }
      });
      return latestEnd;
    } catch (e) {
      return null;
    }
  };

  // Auto-close stale entries
  const autoCloseStale = async () => {
    const now = new Date();
    for (const [staffId, entry] of Object.entries(activeEntries)) {
      // Auto-end break if too long
      if (entry.status === 'on-break' && entry.breaks?.length > 0) {
        const lastBreak = entry.breaks[entry.breaks.length - 1];
        if (lastBreak.startTime && !lastBreak.endTime) {
          const breakStart = lastBreak.startTime.toDate ? lastBreak.startTime.toDate() : new Date(lastBreak.startTime);
          if ((now - breakStart) / (1000 * 60) > AUTO_BREAK_CLOSE_MINUTES) {
            try { await endBreak(entry.id); } catch (e) { console.error(e); }
          }
        }
      }
      // Auto-checkout based on schedule end time or 6 PM fallback
      const scheduled = await getScheduledEndTime(staffId);
      const autoHour = scheduled ? scheduled.hours : FALLBACK_AUTO_CHECKOUT_HOUR;
      const autoMinute = scheduled ? scheduled.minutes : 0;
      if (now.getHours() > autoHour || (now.getHours() === autoHour && now.getMinutes() >= autoMinute)) {
        try {
          await checkOut(entry.id, selectedLocation, null);
          await updateDoc(doc(db, 'time_entries', entry.id), {
            autoCheckedOut: true,
            autoCheckOutReason: scheduled ? 'schedule-end' : 'fallback-6pm',
            updatedAt: serverTimestamp(),
          });
        } catch (e) { console.error('Auto checkout failed', e); }
      }
    }
    await loadAll();
  };

  useEffect(() => {
    const t = setInterval(autoCloseStale, 60000);
    return () => clearInterval(t);
  }, [activeEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckIn = async (s) => {
    if (processingRef.current[s.id]) return;
    processingRef.current[s.id] = true;
    setActionLoading(s.id);
    try {
      await checkIn(s.id, `${s.firstName} ${s.lastName}`, selectedLocation, null);
      await loadAll();
      showMessage(`✅ ${displayName(s)} checked in`);
    } catch (err) {
      showMessage(`❌ ${err.message}`, 'error');
    } finally {
      setActionLoading(null);
      setTimeout(() => { processingRef.current[s.id] = false; }, 1000);
    }
  };

  const handleCheckOut = async (s, entry) => {
    if (processingRef.current[s.id]) return;
    processingRef.current[s.id] = true;
    setActionLoading(s.id);
    try {
      await checkOut(entry.id, selectedLocation, null);
      await loadAll();
      const hours = entry.checkInTime ? ((Date.now() - entry.checkInTime.toMillis()) / (1000 * 60 * 60)).toFixed(2) : '?';
      showMessage(`👋 ${displayName(s)} checked out — ${hours} hrs`);
    } catch (err) {
      showMessage(`❌ ${err.message}`, 'error');
    } finally {
      setActionLoading(null);
      setTimeout(() => { processingRef.current[s.id] = false; }, 1000);
    }
  };

  const handleBreak = async (s, entry) => {
    if (processingRef.current[s.id]) return;
    processingRef.current[s.id] = true;
    setActionLoading(s.id);
    try {
      if (entry.status === 'on-break') {
        // End break — show duration alert if over limit
        const activeBreak = [...(entry.breaks || [])].reverse().find(b => !b.endTime);
        const isLunchBreak = activeBreak?.type === 'unpaid-meal';
        const startMs = activeBreak?.startTime?.toMillis ? activeBreak.startTime.toMillis() : null;
        await endBreak(entry.id);
        if (startMs) {
          const durationMins = Math.floor((Date.now() - startMs) / 60000);
          const limit = isLunchBreak ? 32 : 12;
          const overBy = durationMins - limit;
          if (overBy > 0) {
            showMessage(`⚠️ ${displayName(s)} break was ${durationMins}m — ${overBy}m over`, 'error');
          } else {
            showMessage(`✅ ${displayName(s)} returned — ${durationMins}m break`);
          }
        } else {
          showMessage(`✅ ${displayName(s)} returned from break`);
        }
      } else {
        // Determine next break type sequentially — respects shift length
        const breaks = entry.breaks || [];
        const paidBreaksTaken = breaks.filter(b => b.type === 'paid-rest').length;
        const lunchTaken = breaks.some(b => b.type === 'unpaid-meal');
        const shiftHours = staffShiftHours[s.id] ?? null;
        const isFullShift = shiftHours === null || shiftHours > 4;
        let breakType = 'paid-rest';
        let breakLabel = '10-Min Break';
        if (isFullShift && paidBreaksTaken >= 1 && !lunchTaken) {
          breakType = 'unpaid-meal';
          breakLabel = 'Lunch Break (30 min)';
        } else if (isFullShift && paidBreaksTaken >= 1 && lunchTaken) {
          breakType = 'paid-rest';
          breakLabel = '10-Min Break 2';
        }
        await startBreak(entry.id, breakType);
        showMessage(`⏸ ${displayName(s)} started ${breakLabel}`);
      }
      await loadAll();
    } catch (err) {
      showMessage(`❌ ${err.message}`, 'error');
    } finally {
      setActionLoading(null);
      setTimeout(() => { processingRef.current[s.id] = false; }, 1000);
    }
  };

  const getElapsed = (entry) => {
    if (!entry?.checkInTime) return '';
    const mins = (Date.now() - entry.checkInTime.toMillis()) / (1000 * 60);
    return `${Math.floor(mins / 60)}h ${Math.floor(mins % 60)}m`;
  };


  const [selectedStaffId, setSelectedStaffId] = useState('');
  const selectedMember = staff.find(s => s.id === selectedStaffId);
  const selectedEntry = selectedStaffId ? activeEntries[selectedStaffId] : null;
  const isSelectedActive = !!selectedEntry;
  const isSelectedOnBreak = selectedEntry?.status === 'on-break';
  const isProcessing = actionLoading === selectedStaffId;

  const sortedStaff = [...staff].sort((a, b) => a.firstName.localeCompare(b.firstName));
  const notCheckedIn = sortedStaff.filter(s => !activeEntries[s.id]);
  const checkedIn = sortedStaff.filter(s => !!activeEntries[s.id]);

  // Filter checked-in staff by selected location for display
  const checkedInAtLocation = checkedIn.filter(s => {
    const entry = activeEntries[s.id];
    if (!entry) return false;
    const loc = (entry.checkInLocation || '').toLowerCase();
    return loc === selectedLocation || loc.includes(selectedLocation);
  });
  const checkedInCount = checkedInAtLocation.length;
  const totalCheckedIn = checkedIn.length;

  return (
    <div style={{ minHeight: '60vh' }}>
      {/* Status bar */}
      {/* Location Selector */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { value: 'redmond', label: '📍 Redmond' },
          { value: 'kirkland', label: '📍 Kirkland' },
        ].map(loc => (
          <button
            key={loc.value}
            onClick={() => setSelectedLocation(loc.value)}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: `2px solid ${selectedLocation === loc.value ? '#3b82f6' : '#e5e7eb'}`,
              borderRadius: '8px',
              backgroundColor: selectedLocation === loc.value ? '#eff6ff' : 'white',
              color: selectedLocation === loc.value ? '#1e40af' : '#6b7280',
              fontWeight: selectedLocation === loc.value ? '700' : '500',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {loc.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Clock size={16} style={{ color: '#6b7280' }} />
          <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '600' }}>
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <span style={{ fontSize: '0.8rem', backgroundColor: '#d1fae5', color: '#065f46', padding: '0.2rem 0.6rem', borderRadius: '4px', fontWeight: '700' }}>
          {checkedInAtLocation.length} staff at {selectedLocation === 'redmond' ? 'Redmond' : 'Kirkland'} · {totalCheckedIn} total
        </span>
      </div>

      {/* Toast */}
      {message && (
        <div style={{ position: 'fixed', top: '1.5rem', left: '50%', transform: 'translateX(-50%)', backgroundColor: message.type === 'error' ? '#dc2626' : '#10b981', color: 'white', padding: '0.85rem 1.5rem', borderRadius: '10px', fontWeight: '700', fontSize: '1rem', zIndex: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '3rem' }}>Loading staff...</div>
      ) : (
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>

          {/* LEFT — Check In Panel */}
          <div style={{ flex: '0 0 300px', minWidth: 0 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
              Check In
            </div>
            <select
              value={selectedStaffId}
              onChange={e => setSelectedStaffId(e.target.value)}
              style={{ width: '100%', padding: '0.85rem 1rem', fontSize: '1rem', border: '2px solid #d1d5db', borderRadius: '10px', backgroundColor: 'white', color: '#1f2937', fontWeight: '500', cursor: 'pointer', outline: 'none', marginBottom: '1rem' }}
            >
              <option value="">— Select your name —</option>
              {notCheckedIn.map(s => (
                <option key={s.id} value={s.id}>{s.firstName} {(s.lastName || '').charAt(0)}.</option>
              ))}
              {checkedIn.length > 0 && (
                <optgroup label="Already checked in">
                  {checkedIn.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.firstName} {(s.lastName || '').charAt(0)}. {activeEntries[s.id]?.status === 'on-break' ? '☕' : '✅'}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {selectedMember && (
              <div style={{ backgroundColor: isSelectedOnBreak ? '#fffbeb' : isSelectedActive ? '#f0fdf4' : 'white', border: `2px solid ${isSelectedOnBreak ? '#f59e0b' : isSelectedActive ? '#10b981' : '#e5e7eb'}`, borderRadius: '12px', padding: '1.25rem' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1f2937', marginBottom: '0.5rem' }}>
                  {selectedMember.firstName} {(selectedMember.lastName || '').charAt(0)}.
                </div>
                {isSelectedActive ? (
                  <div style={{ fontSize: '0.82rem', color: isSelectedOnBreak ? '#d97706' : '#059669', fontWeight: '600', marginBottom: '1rem' }}>
                    {isSelectedOnBreak ? (getBreakLabel(selectedEntry) || '☕ On Break') : `✅ In since ${fmt12(selectedEntry.checkInTime)} — ${getElapsed(selectedEntry)}`}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '1rem' }}>Not checked in today</div>
                )}

                {!isSelectedActive ? (
                  <button onClick={() => handleCheckIn(selectedMember)} disabled={isProcessing} style={{ width: '100%', padding: '0.9rem', border: 'none', borderRadius: '9px', backgroundColor: isProcessing ? '#9ca3af' : '#10b981', color: 'white', fontWeight: '800', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <LogIn size={18} /> {isProcessing ? 'Checking in...' : 'Check In'}
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    <button onClick={() => handleBreak(selectedMember, selectedEntry)} disabled={isProcessing} style={{ width: '100%', padding: '0.75rem', border: 'none', borderRadius: '9px', backgroundColor: isSelectedOnBreak ? '#f59e0b' : '#f3f4f6', color: isSelectedOnBreak ? 'white' : '#6b7280', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} title={!isSelectedOnBreak ? (() => { const b = selectedEntry?.breaks||[]; const p = b.filter(x=>x.type==='paid-rest').length; const l = b.some(x=>x.type==='unpaid-meal'); const sh = staffShiftHours[selectedMember?.id]??null; const full=sh===null||sh>4; return full&&p>=1&&!l?'Start Lunch Break':full&&p>=1&&l?'Start 10-Min Break 2':'Start 10-Min Break'; })() : 'End Break'}>
                      <Coffee size={16} /> {isSelectedOnBreak ? 'End Break' : 'Start Break'}
                    </button>
                    <button onClick={() => handleCheckOut(selectedMember, selectedEntry)} disabled={isProcessing} style={{ width: '100%', padding: '0.75rem', border: 'none', borderRadius: '9px', backgroundColor: isProcessing ? '#9ca3af' : '#dc2626', color: 'white', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <LogOut size={16} /> {isProcessing ? 'Processing...' : 'Check Out'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Currently Checked In */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
              Currently In at {selectedLocation === 'redmond' ? '📍 Redmond' : '📍 Kirkland'} ({checkedInAtLocation.length}){totalCheckedIn > checkedInAtLocation.length ? ` · ${totalCheckedIn - checkedInAtLocation.length} at other location` : ''}
            </div>
            {checkedInAtLocation.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                No staff checked in yet today
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.65rem' }}>
                {checkedInAtLocation.map(s => {
                  const entry = activeEntries[s.id];
                  const onBreak = entry?.status === 'on-break';
                  const proc = actionLoading === s.id;
                  return (
                    <div key={s.id} style={{ backgroundColor: onBreak ? '#fffbeb' : '#f0fdf4', border: `2px solid ${onBreak ? '#f59e0b' : '#10b981'}`, borderRadius: '10px', padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>
                          {s.firstName} {(s.lastName || '').charAt(0)}.
                        </div>
                        <div style={{ fontSize: '0.68rem', color: onBreak ? '#d97706' : '#059669', fontWeight: '600' }}>
                          {onBreak ? (getBreakLabel(entry) || '☕ On Break') : `✅ ${getElapsed(entry)}`}
                        </div>
                        <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>In: {fmt12(entry.checkInTime)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => handleBreak(s, entry)} disabled={proc} style={{ flex: 1, padding: '0.4rem', border: 'none', borderRadius: '6px', backgroundColor: onBreak ? '#f59e0b' : '#e5e7eb', color: onBreak ? 'white' : '#6b7280', fontWeight: '600', fontSize: '0.7rem', cursor: 'pointer' }}>
                          {onBreak ? 'End' : (() => { const b = entry.breaks||[]; const p = b.filter(x=>x.type==='paid-rest').length; const l = b.some(x=>x.type==='unpaid-meal'); const sh=staffShiftHours[s.id]??null; const full=sh===null||sh>4; return full&&p>=1&&!l?'🍽️':full&&p>=1&&l?'☕2':'☕'; })()}
                        </button>
                        <button onClick={() => handleCheckOut(s, entry)} disabled={proc} style={{ flex: 1, padding: '0.4rem', border: 'none', borderRadius: '6px', backgroundColor: '#dc2626', color: 'white', fontWeight: '700', fontSize: '0.7rem', cursor: 'pointer' }}>
                          Out
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default StaffKiosk;